import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';

function Point4({ projectId }) {
  const [activeTab, setActiveTab] = useState(0);
  const [marketCriteria, setMarketCriteria] = useState(null);
  const [countriesAnalysis, setCountriesAnalysis] = useState(null);
  const [comparisonMatrix, setComparisonMatrix] = useState(null);
  const [finalRecommendations, setFinalRecommendations] = useState(null);
  const [quickAnalysis, setQuickAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);

  const [criteriaTemplate, setCriteriaTemplate] = useState('Criterios Generales');
  const [criteriaPrompt, setCriteriaPrompt] = useState('');
  const [countriesList, setCountriesList] = useState('España, Francia, Alemania, Italia, Reino Unido, Portugal');
  const [analysisDepth, setAnalysisDepth] = useState('Análisis Básico');
  const [scoringMethod, setScoringMethod] = useState('Puntuación 1-10');
  const [includeWeights, setIncludeWeights] = useState(true);
  const [recommendationFocus, setRecommendationFocus] = useState('Recomendaciones Generales');
  const [includeTimeline, setIncludeTimeline] = useState(true);
  const [includeResources, setIncludeResources] = useState(true);
  const [includeRisks, setIncludeRisks] = useState(true);

  useEffect(() => {
    loadStoredData();
  }, [projectId]);

  useEffect(() => {
    setCriteriaPrompt(criteriaTemplates[criteriaTemplate]);
  }, [criteriaTemplate]);

  const criteriaTemplates = {
    'Criterios Generales': `Define los criterios para seleccionar mercados objetivo:

CRITERIOS ECONÓMICOS:
- PIB y crecimiento económico
- Poder adquisitivo de la población
- Estabilidad económica y monetaria
- Facilidad para hacer negocios

CRITERIOS DE MERCADO:
- Tamaño del mercado objetivo
- Tasa de crecimiento del sector
- Nivel de competencia existente
- Barreras de entrada

CRITERIOS CULTURALES/POLÍTICOS:
- Estabilidad política y legal
- Proximidad cultural y lingüística
- Regulaciones específicas del sector
- Acuerdos comerciales existentes

CRITERIOS LOGÍSTICOS:
- Distancia geográfica
- Infraestructura de transporte
- Costes logísticos
- Facilidad de distribución`,

    'Criterios B2B': `Criterios específicos para mercados B2B:

CRITERIOS DE DEMANDA:
- Número de empresas potencialmente clientes
- Volumen de compra promedio
- Frecuencia de renovación
- Tendencias de crecimiento sectorial

CRITERIOS COMPETITIVOS:
- Presencia de competidores directos
- Nivel de sofisticación del mercado
- Barreras tecnológicas
- Diferenciación posible

CRITERIOS COMERCIALES:
- Canales de distribución disponibles
- Márgenes comerciales esperados
- Plazos de pago habituales
- Necesidad de inversión inicial`,

    'Criterios B2C': `Criterios específicos para mercados B2C:

CRITERIOS DEMOGRÁFICOS:
- Tamaño del segmento objetivo
- Edad y perfil del consumidor
- Nivel de ingresos
- Hábitos de consumo

CRITERIOS DE COMPORTAMIENTO:
- Canales de compra preferidos
- Sensibilidad al precio
- Lealtad a la marca
- Influencia digital/redes sociales

CRITERIOS DE PRODUCTO:
- Adaptación necesaria del producto
- Percepción de calidad
- Necesidades específicas locales
- Ciclo de vida del producto`,

    'Personalizado': ''
  };

  const analysisDepthTemplates = {
    'Análisis Básico': `Para cada país analiza:
- Datos económicos básicos (PIB, población, crecimiento)
- Facilidad para hacer negocios
- Principales sectores económicos
- Estabilidad política básica`,
    
    'Análisis Detallado': `Para cada país realiza un análisis detallado:
- Indicadores macroeconómicos completos
- Análisis del sector específico de la empresa
- Competencia local identificada
- Barreras de entrada específicas
- Canales de distribución disponibles
- Requisitos legales y regulatorios`,
    
    'Análisis Completo': `Para cada país proporciona análisis exhaustivo:
- Entorno macroeconómico y tendencias
- Análisis sectorial profundo con datos
- Mapa competitivo detallado
- Análisis cultural y comportamiento consumidor
- Infraestructura y logística
- Marco legal y fiscal específico
- Oportunidades y riesgos identificados
- Recomendaciones específicas de entrada`
  };

  const focusPrompts = {
    'Recomendaciones Generales': 'Proporciona recomendaciones equilibradas considerando oportunidades y riesgos',
    'Enfoque Conservador': 'Prioriza mercados de menor riesgo, entrada gradual e inversión mínima',
    'Enfoque Agresivo': 'Identifica mercados de alto potencial, entrada rápida y mayor inversión',
    'Estrategia Gradual': 'Propón una secuencia de entrada por fases, del mercado más fácil al más complejo'
  };

  const loadStoredData = () => {
    setMarketCriteria(getFromStorage(`market_criteria_${projectId}`));
    setCountriesAnalysis(getFromStorage(`countries_analysis_${projectId}`));
    setComparisonMatrix(getFromStorage(`comparison_matrix_${projectId}`));
    setFinalRecommendations(getFromStorage(`final_recommendations_${projectId}`));
    setQuickAnalysis(getFromStorage(`quick_analysis_${projectId}`));
  };

  const getFromStorage = (key) => {
    const data = localStorage.getItem(key);
    return data ? JSON.parse(data) : null;
  };

  const saveToStorage = (key, data) => {
    localStorage.setItem(key, JSON.stringify(data));
  };

  const analyzeWithLLM = async (pointNumber, prompt, useWeb = false, searchTerms = '') => {
    setLoading(true);
    try {
      const response = await axios.post('/api/analysis/', {
        project_id: projectId,
        point_number: pointNumber,
        prompt,
        use_web: useWeb,
        search_terms: searchTerms,
        web_only: false
      });
      return response.data;
    } catch (error) {
      console.error('Error en análisis:', error);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const defineCriteria = async () => {
    if (!criteriaPrompt.trim()) return;
    
    const result = await analyzeWithLLM(
      41, 
      criteriaPrompt, 
      true, 
      'criterios selección mercados internacionales'
    );
    
    if (result) {
      setMarketCriteria(result);
      saveToStorage(`market_criteria_${projectId}`, result);
    }
  };

  const analyzeCountries = async () => {
    const countries = countriesList.split(',').map(c => c.trim()).filter(c => c);
    if (!countries.length) return;

    const prompt = `${analysisDepthTemplates[analysisDepth]}

Países a analizar: ${countries.join(', ')}

Estructura el análisis país por país de forma clara y comparable.`;

    const searchTerms = `mercado ${countries.join(' ')} sector análisis económico`;
    
    const result = await analyzeWithLLM(42, prompt, true, searchTerms);
    
    if (result) {
      setCountriesAnalysis(result);
      saveToStorage(`countries_analysis_${projectId}`, result);
    }
  };

  const generateMatrix = async () => {
    if (!marketCriteria || !countriesAnalysis) return;

    const prompt = `Genera una matriz comparativa de mercados basándote en:

CRITERIOS DEFINIDOS:
${marketCriteria.content}

ANÁLISIS DE PAÍSES:
${countriesAnalysis.content}

INSTRUCCIONES:
- Crea una tabla comparativa con países en filas y criterios en columnas
- Usa escala: ${scoringMethod}
- ${includeWeights ? 'Incluye peso/importancia de cada criterio' : 'Sin pesos, todos los criterios igual importancia'}
- Proporciona justificación para cada puntuación
- Calcula puntuación total por país
- Ordena países por puntuación final

FORMATO:
1. Tabla comparativa clara
2. Justificaciones por criterio y país
3. Ranking final con recomendaciones`;

    const result = await analyzeWithLLM(43, prompt, false);
    
    if (result) {
      setComparisonMatrix(result);
      saveToStorage(`comparison_matrix_${projectId}`, result);
    }
  };

  const quickMarketAnalysis = async () => {
    const prompt = `Realiza una selección de mercados internacionales recomendados:

1. ANÁLISIS DE CRITERIOS: Define criterios relevantes para seleccionar mercados
2. MERCADOS CANDIDATOS: Identifica 5-8 países/mercados más prometedores
3. EVALUACIÓN: Analiza cada mercado según los criterios definidos
4. MATRIZ COMPARATIVA: Crea tabla con puntuaciones 1-10
5. RECOMENDACIONES: Ranking final con top 3 mercados recomendados

Base el análisis en la información de la empresa disponible.`;

    const result = await analyzeWithLLM(
      44, 
      prompt, 
      true, 
      'selección mercados internacionales exportación'
    );
    
    if (result) {
      setQuickAnalysis(result);
      saveToStorage(`quick_analysis_${projectId}`, result);
    }
  };

  const generateRecommendations = async () => {
    const baseAnalysis = comparisonMatrix?.content || quickAnalysis?.content;
    if (!baseAnalysis) return;

    const prompt = `Basándote en el análisis de mercados realizado, genera recomendaciones estratégicas:

ANÁLISIS BASE:
${baseAnalysis}

ENFOQUE: ${focusPrompts[recommendationFocus]}

ESTRUCTURA DE RECOMENDACIONES:
1. MERCADOS PRIORITARIOS: Top 3 mercados recomendados con justificación
2. ESTRATEGIA DE ENTRADA: Modalidad recomendada para cada mercado
${includeTimeline ? '3. CRONOGRAMA: Fases de implementación con timeline' : ''}
${includeResources ? '4. RECURSOS NECESARIOS: Inversión, personal, infraestructura' : ''}
${includeRisks ? '5. RIESGOS Y MITIGACIÓN: Principales riesgos y cómo minimizarlos' : ''}
6. FACTORES CRÍTICOS DE ÉXITO: Qué vigilar para asegurar el éxito
7. PRÓXIMOS PASOS: Acciones inmediatas a tomar

Proporciona recomendaciones específicas y accionables.`;

    const result = await analyzeWithLLM(45, prompt, false);
    
    if (result) {
      setFinalRecommendations(result);
      saveToStorage(`final_recommendations_${projectId}`, result);
    }
  };

  const downloadContent = (content, filename) => {
    const element = document.createElement('a');
    const file = new Blob([content], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = filename;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const tabs = [
    'Criterios de Selección',
    'Análisis de Países',
    'Comparativa',
    'Recomendaciones'
  ];

  return (
    <div className="point-content">
      <h2>4. Selección de Mercados</h2>
      
      <div className="tabs">
        <div className="tab-buttons">
          {tabs.map((tab, index) => (
            <button
              key={index}
              className={`tab-btn ${activeTab === index ? 'active' : ''}`}
              onClick={() => setActiveTab(index)}
            >
              {tab}
            </button>
          ))}
        </div>

        {activeTab === 0 && (
          <div className="tab-content">
            <h3>Definición de Criterios de Selección</h3>
            
            <div className="two-cols">
              <div className="col-left">
                <h4>Plantillas de criterios:</h4>
                
                <div className="template-selector">
                  <label>Seleccionar plantilla:</label>
                  <select 
                    value={criteriaTemplate}
                    onChange={(e) => setCriteriaTemplate(e.target.value)}
                  >
                    {Object.keys(criteriaTemplates).map(template => (
                      <option key={template} value={template}>{template}</option>
                    ))}
                  </select>
                </div>

                <button 
                  onClick={defineCriteria}
                  disabled={loading || !criteriaPrompt.trim()}
                  className="analyze-btn"
                >
                  {loading ? 'Definiendo criterios...' : 'Definir Criterios'}
                </button>
              </div>

              <div className="col-right">
                <h4>Prompt para criterios:</h4>
                <textarea
                  value={criteriaPrompt}
                  onChange={(e) => setCriteriaPrompt(e.target.value)}
                  rows={15}
                />
              </div>
            </div>

            {marketCriteria && (
              <div className="analysis-result">
                <h4>Criterios Definidos</h4>
                <div className="result-content"><ReactMarkdown>{marketCriteria.content}</ReactMarkdown></div>
              </div>
            )}
          </div>
        )}

        {activeTab === 1 && (
          <div className="tab-content">
            <h3>Análisis de Países Candidatos</h3>
            
            <div className="two-cols">
              <div className="col-left">
                <h4>Países a analizar:</h4>
                
                <textarea
                  value={countriesList}
                  onChange={(e) => setCountriesList(e.target.value)}
                  placeholder="Lista de países (separados por comas)"
                  rows={4}
                />

                <div className="template-selector">
                  <label>Profundidad del análisis:</label>
                  <select 
                    value={analysisDepth}
                    onChange={(e) => setAnalysisDepth(e.target.value)}
                  >
                    {Object.keys(analysisDepthTemplates).map(depth => (
                      <option key={depth} value={depth}>{depth}</option>
                    ))}
                  </select>
                </div>

                <button 
                  onClick={analyzeCountries}
                  disabled={loading || !countriesList.trim()}
                  className="analyze-btn"
                >
                  {loading ? 'Analizando países...' : 'Analizar Países'}
                </button>
              </div>

              <div className="col-right">
                {countriesAnalysis ? (
                  <div className="analysis-result">
                    <h4>Análisis de Países</h4>
                    <div className="result-content"><ReactMarkdown>{countriesAnalysis.content}</ReactMarkdown></div>
                    {countriesAnalysis.sources && (
                      <div className="sources">
                        <h5>Fuentes utilizadas:</h5>
                        <ul>
                          {countriesAnalysis.sources.map((source, index) => (
                            <li key={index}>{source}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="info-box">
                    Selecciona países y ejecuta el análisis para ver resultados
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 2 && (
          <div className="tab-content">
            <h3>Matriz Comparativa de Mercados</h3>
            
            {marketCriteria && countriesAnalysis ? (
              <div className="two-cols">
                <div className="col-left">
                  <div className="info-box success">
                    Criterios y países analizados
                  </div>

                  <div className="template-selector">
                    <label>Método de puntuación:</label>
                    <select 
                      value={scoringMethod}
                      onChange={(e) => setScoringMethod(e.target.value)}
                    >
                      <option value="Puntuación 1-10">Puntuación 1-10</option>
                      <option value="Puntuación 1-5">Puntuación 1-5</option>
                      <option value="Alto/Medio/Bajo">Alto/Medio/Bajo</option>
                    </select>
                  </div>

                  <label>
                    <input
                      type="checkbox"
                      checked={includeWeights}
                      onChange={(e) => setIncludeWeights(e.target.checked)}
                    />
                    Incluir pesos por criterio
                  </label>

                  <button 
                    onClick={generateMatrix}
                    disabled={loading}
                    className="analyze-btn"
                  >
                    {loading ? 'Generando matriz...' : 'Generar Matriz Comparativa'}
                  </button>
                </div>

                <div className="col-right">
                  {comparisonMatrix ? (
                    <div className="analysis-result">
                      <h4>Matriz Comparativa</h4>
                      <div className="result-content"><ReactMarkdown>{comparisonMatrix.content}</ReactMarkdown></div>
                      <button 
                        onClick={() => downloadContent(comparisonMatrix.content, 'Matriz_Mercados.txt')}
                        className="analyze-btn secondary"
                      >
                        Exportar Matriz
                      </button>
                    </div>
                  ) : (
                    <div className="info-box">
                      Genera la matriz comparativa para ver resultados
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div>
                <div className="info-box warning">
                  Completa primero: {!marketCriteria && 'criterios de selección'} 
                  {!marketCriteria && !countriesAnalysis && ' y '} 
                  {!countriesAnalysis && 'análisis de países'}
                </div>

                <button 
                  onClick={quickMarketAnalysis}
                  disabled={loading}
                  className="analyze-btn"
                >
                  {loading ? 'Analizando...' : 'Análisis Rápido de Mercados'}
                </button>

                {quickAnalysis && (
                  <div className="analysis-result">
                    <h4>Análisis Rápido de Mercados</h4>
                    <div className="result-content"><ReactMarkdown>{quickAnalysis.content}</ReactMarkdown></div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 3 && (
          <div className="tab-content">
            <h3>Recomendaciones Finales</h3>
            
            {(comparisonMatrix || quickAnalysis) ? (
              <div className="two-cols">
                <div className="col-left">
                  <h4>Generar recomendaciones:</h4>

                  <div className="template-selector">
                    <label>Enfoque de recomendaciones:</label>
                    <select 
                      value={recommendationFocus}
                      onChange={(e) => setRecommendationFocus(e.target.value)}
                    >
                      {Object.keys(focusPrompts).map(focus => (
                        <option key={focus} value={focus}>{focus}</option>
                      ))}
                    </select>
                  </div>

                  <label>
                    <input
                      type="checkbox"
                      checked={includeTimeline}
                      onChange={(e) => setIncludeTimeline(e.target.checked)}
                    />
                    Incluir cronograma
                  </label>

                  <label>
                    <input
                      type="checkbox"
                      checked={includeResources}
                      onChange={(e) => setIncludeResources(e.target.checked)}
                    />
                    Incluir recursos necesarios
                  </label>

                  <label>
                    <input
                      type="checkbox"
                      checked={includeRisks}
                      onChange={(e) => setIncludeRisks(e.target.checked)}
                    />
                    Incluir análisis de riesgos
                  </label>

                  <button 
                    onClick={generateRecommendations}
                    disabled={loading}
                    className="analyze-btn"
                  >
                    {loading ? 'Generando recomendaciones...' : 'Generar Recomendaciones'}
                  </button>
                </div>

                <div className="col-right">
                  {finalRecommendations ? (
                    <div className="analysis-result">
                      <h4>Recomendaciones Estratégicas</h4>
                      <div className="result-content"><ReactMarkdown>{finalRecommendations.content}</ReactMarkdown></div>
                      
                      <div className="analysis-buttons">
                        <button 
                          onClick={() => downloadContent(finalRecommendations.content, 'Recomendaciones_Mercados.txt')}
                          className="analyze-btn secondary"
                        >
                          Exportar Recomendaciones
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="info-box">
                      Genera las recomendaciones para ver resultados
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="info-box warning">
                Realiza primero el análisis de mercados en las pestañas anteriores para generar recomendaciones
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default Point4;